import { evaluateGuess, GameState, getElapsedSeconds, getMaxGuesses, Stats, LetterState, DailyLevel } from "./engine";
import { getDailyAnswer } from "./daily";

export interface LeaderboardRow {
  name: string;
  streak: number;
  wins: number;
  score: number;
  guesses: number;
}

export interface BackendAdapter {
  enabled: boolean;
  uid?: string;
  startDailySession(game: GameState): Promise<Partial<GameState>>;
  submitGuess(game: GameState, guess: string): Promise<Partial<GameState>>;
  loadStats(fallback: Stats): Promise<Stats>;
  saveStats(stats: Stats): Promise<void>;
  submitDailyResult(game: GameState, stats: Stats): Promise<void>;
  submitUnlimitedResult(stats: Stats): Promise<void>;
  getDailyLeaderboard(dateKey: string, level?: DailyLevel): Promise<LeaderboardRow[]>;
  getUnlimitedLeaderboard(): Promise<LeaderboardRow[]>;
}

const FIREBASE_PUBLIC_CONFIG = {
  apiKey: "AIzaSyD8jNlSwJJw5hFw4mwKr3_B2hSQRn_t_3M",
  authDomain: "lexilink-game.firebaseapp.com",
  projectId: "lexilink-game",
  storageBucket: "lexilink-game.firebasestorage.app",
  messagingSenderId: "387823778041",
  appId: "1:387823778041:web:4eaaea8c220feb89f2ca28"
};

function firebaseConfigured() {
  return Boolean((import.meta.env.VITE_FIREBASE_API_KEY || FIREBASE_PUBLIC_CONFIG.apiKey) && (import.meta.env.VITE_FIREBASE_PROJECT_ID || FIREBASE_PUBLIC_CONFIG.projectId));
}

function appCheckConfigured() {
  return Boolean(import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY);
}

let backendPromise: Promise<BackendAdapter> | undefined;

export function getBackend() {
  backendPromise ??= createBackend();
  return backendPromise;
}

async function createBackend(): Promise<BackendAdapter> {
  if (!firebaseConfigured()) {
    return localBackend;
  }

  const [{ initializeApp }, { getAuth, signInAnonymously }, firestore, functions] = await Promise.all([
    import("firebase/app"),
    import("firebase/auth"),
    import("firebase/firestore"),
    import("firebase/functions")
  ]);

  const app = initializeApp({
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || FIREBASE_PUBLIC_CONFIG.apiKey,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || FIREBASE_PUBLIC_CONFIG.authDomain,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || FIREBASE_PUBLIC_CONFIG.projectId,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || FIREBASE_PUBLIC_CONFIG.storageBucket,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || FIREBASE_PUBLIC_CONFIG.messagingSenderId,
    appId: import.meta.env.VITE_FIREBASE_APP_ID || FIREBASE_PUBLIC_CONFIG.appId
  });

  if (appCheckConfigured()) {
    if (import.meta.env.DEV && import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN) {
      const token = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN;
      (globalThis as typeof globalThis & { FIREBASE_APPCHECK_DEBUG_TOKEN?: true | string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
        token === "true" ? true : token;
    }

    const { initializeAppCheck, ReCaptchaV3Provider } = await import("firebase/app-check");
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true
    });
  }

  const auth = getAuth(app);
  const credential = await signInAnonymously(auth);
  const uid = credential.user.uid;
  const db = firestore.getFirestore(app);
  const callableFunctions = functions.getFunctions(app);

  const rowFromDoc = (snapshot: { data: () => Record<string, unknown> | undefined }): LeaderboardRow => {
    const data = snapshot.data() ?? {};
    return {
      name: String(data.name ?? "Player"),
      streak: Number(data.streak ?? 0),
      wins: Number(data.wins ?? 0),
      score: Number(data.score ?? 0),
      guesses: Number(data.guesses ?? 0)
    };
  };

  return {
    enabled: true,
    uid,
    async startDailySession(game) {
      const startDailySession = functions.httpsCallable(callableFunctions, "startDailySession");
      const result = await startDailySession({
        dateKey: game.dateKey,
        timezoneOffset: new Date().getTimezoneOffset(),
        level: game.level
      });
      const data = result.data as Partial<GameState>;
      return {
        guesses: data.guesses ?? [],
        evaluations: data.evaluations as LetterState[][] | undefined,
        status: data.status ?? "playing",
        completedAt: data.completedAt
      };
    },
    async submitGuess(game, guess) {
      const submitDailyGuess = functions.httpsCallable(callableFunctions, "submitDailyGuess");
      const result = await submitDailyGuess({
        dateKey: game.dateKey,
        timezoneOffset: new Date().getTimezoneOffset(),
        level: game.level,
        guess
      });
      const data = result.data as Partial<GameState>;
      return {
        guesses: data.guesses ?? [],
        evaluations: data.evaluations as LetterState[][] | undefined,
        status: data.status ?? "playing",
        answer: data.answer,
        completedAt: data.completedAt ?? (data.status === "playing" ? undefined : Date.now())
      };
    },
    async loadStats(fallback) {
      const userRef = firestore.doc(db, "users", uid);
      const snapshot = await firestore.getDoc(userRef);
      return snapshot.exists() ? ({ ...fallback, ...(snapshot.data().stats as Partial<Stats>) } as Stats) : fallback;
    },
    async saveStats(stats) {
      await firestore.setDoc(
        firestore.doc(db, "users", uid),
        {
          stats,
          updatedAt: firestore.serverTimestamp()
        },
        { merge: true }
      );
    },
    async submitDailyResult(game, stats) {
      if (game.mode !== "daily" || game.status === "playing") return;
      const submitDailyResult = functions.httpsCallable(callableFunctions, "submitDailyResult");
      await submitDailyResult({
        dateKey: game.dateKey,
        timezoneOffset: new Date().getTimezoneOffset(),
        level: game.level,
        status: game.status,
        guesses: game.guesses,
        streak: stats.currentStreak,
        wins: stats.wins,
        hintsUsed: game.hintsUsed,
        elapsedSeconds: getElapsedSeconds(game),
        name: stats.profileName || "Player"
      });
    },
    async submitUnlimitedResult(stats) {
      const submitUnlimitedResult = functions.httpsCallable(callableFunctions, "submitUnlimitedResult");
      await submitUnlimitedResult({
        name: stats.profileName || "Player",
        wins: stats.unlimitedWins ?? 0,
        bestStage: stats.unlimitedBestRun ?? 0
      });
    },
    async getDailyLeaderboard(dateKey, level = "classic") {
      void dateKey;
      void level;
      const scoresRef = firestore.collection(db, "dailyStreakScores");
      const snapshot = await firestore.getDocs(firestore.query(scoresRef, firestore.orderBy("score", "desc"), firestore.limit(25)));
      return snapshot.docs.map(rowFromDoc);
    },
    async getUnlimitedLeaderboard() {
      const scoresRef = firestore.collection(db, "unlimitedScores");
      const snapshot = await firestore.getDocs(firestore.query(scoresRef, firestore.orderBy("score", "desc"), firestore.limit(25)));
      return snapshot.docs.map(rowFromDoc);
    }
  };
}

const localBackend: BackendAdapter = {
  enabled: false,
  async startDailySession() {
    return {};
  },
  async submitGuess(game, guess) {
    const answer = game.answer || getDailyAnswer(game.dateKey);
    const guesses = [...game.guesses, guess];
    const evaluations = [...(game.evaluations ?? []), evaluateGuess(guess, answer).map((item) => item.state)];
    const status = guess === answer ? "won" : guesses.length >= getMaxGuesses(game.level) ? "lost" : "playing";
    return {
      guesses,
      evaluations,
      status,
      answer,
      completedAt: status === "playing" ? undefined : Date.now()
    };
  },
  async loadStats(fallback) {
    return fallback;
  },
  async saveStats() {},
  async submitDailyResult() {},
  async submitUnlimitedResult() {},
  async getDailyLeaderboard() {
    return [];
  },
  async getUnlimitedLeaderboard() {
    return [];
  }
};
